import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Image as ImageIcon, Film, Sparkles, Loader2, Trash2, Download, RotateCw, Zap } from "lucide-react";

const TABS = [
  { key: "image", label: "Image", icon: ImageIcon },
  { key: "video", label: "Video", icon: Film },
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function StudioDrawer({ open, onClose, pricing, onBalance }) {
  const [tab, setTab] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [gens, setGens] = useState([]);
  const pollRef = useRef(null);

  const cost = pricing?.[tab] ?? 0;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/studio/generations?kind=${tab}`); setGens(data.generations); } catch {}
  }, [tab]);

  useEffect(() => { if (open) load(); }, [open, tab, load]);

  // poll while any video is still generating
  useEffect(() => {
    if (!open) return;
    const hasPending = gens.some((g) => g.status === "generating");
    if (hasPending && !pollRef.current) {
      pollRef.current = setInterval(load, 5000);
    } else if (!hasPending && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [gens, open, load]);

  if (!open) return null;

  const generate = async (p) => {
    const text = (p ?? prompt).trim();
    if (!text) return;
    if (!window.confirm(`Generate this ${tab} for ${cost} Stealth Credits?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/studio/${tab}`, { prompt: text, request_id: `${Date.now()}-${Math.random()}` });
      if (data.balance != null) onBalance(data.balance);
      setPrompt("");
      if (tab === "video") toast("Video is generating — this can take a minute or two.");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "insufficient_credits") toast.error("You're short on credits for this generation.");
      else toast.error("That generation didn't go through. You weren't charged.");
      load();
    } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/studio/generations/${id}`); setGens((x) => x.filter((g) => g.id !== id)); };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <aside data-testid="studio-drawer" className="fixed z-50 top-0 right-0 h-full w-full sm:w-[460px] glass-panel border-l border-white/10 flex flex-col fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="label-mono">Creative Studio</div>
            <h2 className="font-heading text-lg font-medium mt-0.5">Generate image & video</h2>
          </div>
          <button data-testid="studio-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 p-3 border-b border-white/10">
          {TABS.map((t) => { const Icon = t.icon; return (
            <button key={t.key} data-testid={`studio-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-2 text-sm py-2 rounded-full ${tab === t.key ? "text-[#000000] font-medium" : "text-slate-400"}`}
              style={tab === t.key ? { background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" } : {}}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          ); })}
        </div>

        <div className="p-4 border-b border-white/10 space-y-2">
          <textarea data-testid="studio-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder={tab === "image" ? "Describe the image… e.g. a futuristic bedroom in black and silver" : "Describe the video… e.g. a calm ocean at sunset, cinematic"}
            rows={3} className="w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50 resize-none" />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-mono-300" /> {cost} credits</span>
            <button data-testid="studio-generate" onClick={() => generate()} disabled={busy || !prompt.trim()}
              className="rounded-full px-5 py-2 text-sm text-[#000000] font-medium flex items-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-3" data-testid="studio-history">
          {gens.length === 0 && <div className="text-sm text-slate-500 text-center py-8">Nothing generated yet.</div>}
          {gens.map((g) => (
            <div key={g.id} data-testid="studio-generation-item" className="rounded-xl border border-white/10 overflow-hidden bg-white/5">
              {g.status === "completed" && g.url && (tab === "image"
                ? <img src={`${BACKEND}${g.url}`} alt={g.prompt} className="w-full object-cover max-h-64" data-testid="studio-image-result" />
                : <video src={`${BACKEND}${g.url}`} controls className="w-full max-h-64" data-testid="studio-video-result" />)}
              {g.status === "generating" && <div className="flex items-center gap-2 justify-center py-8 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Generating…</div>}
              {g.status === "failed" && <div className="py-6 text-center text-sm text-red-400">Generation failed — you weren't charged.</div>}
              <div className="p-3">
                <div className="text-xs text-slate-300 line-clamp-2">{g.prompt}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-slate-500">{g.cost} credits · {g.status}</span>
                  <div className="flex items-center gap-3">
                    {g.status !== "generating" && <button data-testid="studio-regenerate" onClick={() => generate(g.prompt)} className="text-slate-500 hover:text-mono-300"><RotateCw className="w-3.5 h-3.5" /></button>}
                    {g.status === "completed" && g.url && <a data-testid="studio-download" href={`${BACKEND}${g.url}`} download target="_blank" rel="noreferrer" className="text-slate-500 hover:text-mono-300"><Download className="w-3.5 h-3.5" /></a>}
                    <button data-testid="studio-delete" onClick={() => del(g.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
